/**
 * The **core-bonus pick affordance** the {@link CoreBonusEditor} leaf renders — a thin
 * display layer over the SAME single-source helpers the reducer wall uses
 * (`coreBonusBudget` in `budgets.ts` + `meetsCoreBonusPrereq` in `licenses.ts` from
 * `@lancer-vtt/domain`). So the grey-out can never drift from the wall: the reducer
 * (`set_core_bonus`) still hard-rejects a take that busts the per-LL **budget** or fails
 * the per-manufacturer **prerequisite**; this just makes the player see *why* before
 * clicking.
 *
 * **Mirror, never re-derive (the core invariant).** The budget math (`floor(level/3)`)
 * and the prereq math (`floor(ranksInMfr/3)`) live ONCE in `@lancer-vtt/domain`; this
 * module imports and calls them, it never re-implements the arithmetic. The
 * `alreadyHeld` count the prereq takes is the same per-manufacturer hold count the
 * reducer computes (its `applySetCoreBonus` loop) — resolved here off the compendium
 * because manufacturer is **derived from `source`, never stored**.
 *
 * Five row states, mirroring the reducer's two gates plus the stale-hold case:
 *   - `held` — taken, and still meets its prereq (always for GMS).
 *   - `held-invalid` — taken but now **under-ranked** (a license rank was dropped after
 *     the take). NOT auto-removed (no cross-field cascade); surfaced as a **warning** the
 *     player resolves by clearing it or re-earning the rank.
 *   - `available` — not held, and a take would pass both gates.
 *   - `locked-budget` — not held, no core-bonus budget left this LL.
 *   - `locked-prereq` — not held, the manufacturer-rank prerequisite isn't met.
 */
import {
  coreBonusBudget,
  meetsCoreBonusPrereq,
  GMS_SOURCE,
  type PilotData,
} from "@lancer-vtt/domain";
import type { Compendium } from "@lancer-vtt/compendium";

export type CoreBonusStatus =
  | { state: "held" }
  | { state: "held-invalid"; reason: string }
  | { state: "available" }
  | { state: "locked-budget"; reason: string }
  | { state: "locked-prereq"; reason: string };

/** A locked/invalid state carries a reason; the affordance pure states don't. */
export type CoreBonusRow = {
  id: string;
  name: string;
  source: string | undefined;
  effect: string | undefined;
  status: CoreBonusStatus;
};

/** The header counts the leaf shows, plus a row per compendium core bonus. */
export type CoreBonusAffordance = {
  budget: number;
  used: number;
  atBudget: boolean;
  rows: CoreBonusRow[];
};

/** A core bonus needs `(alreadyHeld + 1) * 3` ranks with its manufacturer (RAW). */
function prereqReason(source: string | undefined, alreadyHeld: number): string {
  return `Requires ${(alreadyHeld + 1) * 3} ${source ?? ""} license rank${(alreadyHeld + 1) * 3 === 1 ? "" : "s"}`.trim();
}

/**
 * The per-bonus pick affordance for the whole core-bonus catalog, in `source`-then-name
 * order. Mirrors `applySetCoreBonus` exactly (budget checked before prereq), so a row the
 * leaf greys is precisely the take the reducer would reject — and a held row it flags is
 * exactly the one whose manufacturer ranks no longer justify it.
 */
export function coreBonusAffordance(pilot: PilotData, compendium: Compendium): CoreBonusAffordance {
  const budget = coreBonusBudget(pilot);
  const used = pilot.coreBonuses.length;
  const atBudget = used >= budget;

  // The pilot's held bonuses, indexed within their manufacturer in take order — the Nth
  // hold from a manufacturer needs `(N) * 3` ranks (so index `i`, 0-based, needs the
  // prereq with `alreadyHeld: i`). Resolve each held id's source off the compendium
  // (manufacturer is derived, never stored), exactly as the reducer does.
  const heldIndexWithinMfr = new Map<string, number>();
  const heldSoFarBySource = new Map<string, number>();
  for (const id of pilot.coreBonuses) {
    const src = compendium.getById("core_bonus", id)?.source ?? "";
    const i = heldSoFarBySource.get(src) ?? 0;
    heldIndexWithinMfr.set(id, i);
    heldSoFarBySource.set(src, i + 1);
  }
  // Total held per manufacturer — the `alreadyHeld` an *unheld* take would face.
  const heldCountBySource = heldSoFarBySource;

  const rows: CoreBonusRow[] = compendium
    .list("core_bonus")
    .slice()
    .sort(
      (a, b) =>
        (a.source ?? "").localeCompare(b.source ?? "") || (a.name ?? "").localeCompare(b.name ?? ""),
    )
    .map((bonus) => {
      const held = pilot.coreBonuses.includes(bonus.id);
      const base = { id: bonus.id, name: bonus.name ?? bonus.id, source: bonus.source, effect: bonus.effect };

      if (held) {
        // GMS / unsourced is always valid; otherwise it's valid iff its take-order index
        // within the manufacturer still satisfies the cumulative rank rule.
        const i = heldIndexWithinMfr.get(bonus.id) ?? 0;
        const stillValid =
          !bonus.source ||
          bonus.source === GMS_SOURCE ||
          meetsCoreBonusPrereq({ pilot, bonus, compendium, alreadyHeld: i });
        return stillValid
          ? { ...base, status: { state: "held" as const } }
          : { ...base, status: { state: "held-invalid" as const, reason: prereqReason(bonus.source, i) } };
      }

      // Not held: budget is the first wall (the reducer checks it before the prereq).
      if (atBudget) {
        return {
          ...base,
          status: {
            state: "locked-budget" as const,
            reason: `Over budget — ${used}/${budget} core bonuses (1 per 3rd LL)`,
          },
        };
      }
      const alreadyHeld = heldCountBySource.get(bonus.source ?? "") ?? 0;
      if (!meetsCoreBonusPrereq({ pilot, bonus, compendium, alreadyHeld })) {
        return { ...base, status: { state: "locked-prereq" as const, reason: prereqReason(bonus.source, alreadyHeld) } };
      }
      return { ...base, status: { state: "available" as const } };
    });

  return { budget, used, atBudget, rows };
}
